import { Check, PlusCircle } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboBoxOption {
  value: string;
  label: string;
}

export interface ComboBoxProps {
  options: ComboBoxOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  creatable?: boolean;
  onCreateOption?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  triggerText?: string;
  isSearchable?: boolean;
}

export function ComboBox({
  options,
  value,
  onChange,
  placeholder = "Select option...",
  emptyText = "No options found.",
  searchPlaceholder = "Search...",
  creatable = false,
  onCreateOption,
  className,
  disabled = false,
  leadingIcon,
  trailingIcon = null,
  triggerText,
  isSearchable = false,
}: ComboBoxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const handleSelect = React.useCallback(
    (currentValue: string) => {
      onChange?.(currentValue === value ? "" : currentValue);
      setOpen(false);
    },
    [onChange, value],
  );

  const handleCreateOption = React.useCallback(() => {
    if (searchValue && onCreateOption) {
      onCreateOption(searchValue);
      setSearchValue("");
      setOpen(false);
    }
  }, [searchValue, onCreateOption]);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={selectedOption ? "default" : "outline"}
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between gap-2 px-3", className)}
          disabled={disabled}
        >
          <div
            className={cn(
              "flex cursor-pointer items-center gap-2 capitalize",
              !selectedOption && "text-muted-foreground",
            )}
          >
            {leadingIcon}
            {selectedOption ? selectedOption.label : triggerText || placeholder}
          </div>
          {trailingIcon}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          {isSearchable && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={setSearchValue}
            />
          )}
          <CommandList>
            <CommandEmpty>
              {creatable && searchValue ? (
                <CommandItem
                  onSelect={handleCreateOption}
                  className="flex items-center gap-2"
                >
                  <PlusCircle className="size-4" />
                  Create "{searchValue}"
                </CommandItem>
              ) : (
                emptyText
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={handleSelect}
                  className="cursor-pointer capitalize"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
